"use client";

import React, {
  memo,
  useMemo,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import {
  Select,
  Combobox,
  Portal,
  useListCollection,
  useFilter,
  HStack,
  Text,
  Stack,
  Tokens,
  Spinner,
  Span,
  useCombobox,
  createListCollection,
} from "@chakra-ui/react";
import { flushSync } from "react-dom";
import type { GenericOptionItem } from "./option.types";

type SelectorInputValueChangeDetails = {
  inputValue: string;
  reason?: string;
};

type SelectorOpenChangeDetails = {
  open: boolean;
  reason?: string;
};

type SelectorValueChangeDetails = {
  value: string[];
};

export type GenericSelectorProps<T> = {
  previewItems: GenericOptionItem[];
  stateObject: T[];
  stateObjectSetter: (value: T[]) => void;
  initValue?: T | null;
  sourceItems?: T[];
  itemKey?: keyof T;
  getItemByValueCustom?: (value: string) => T | undefined;
  allowMultiple?: boolean;
  disabled?: boolean;
  disableClear?: boolean;
  isLoading?: boolean;
  onClear?: () => void;
  onCreateItem?: (label: string) => T | null | undefined;
  createOptionMode?: "prepend" | "append";
  labelText?: string;
  mode?: "select" | "combobox" | "creatable";
  placeholder?: string;
  width?: Tokens["sizes"];
  inputId?: string;
  inputName?: string;
  showDescription?: boolean;
  suppressDefaultStyles?: boolean;
  minWidthItemList?: Tokens["sizes"];
  portalled?: boolean;
  listMaxH?: string;
};

type SelectorListContentProps = {
  portalled: boolean;
  children: React.ReactNode;
};

const NEW_ITEM_VALUE = "[[new]]";
const DEFAULT_MIN_WIDTH_ITEM_LIST = "80";

function LoadingIndicator() {
  return (
    <HStack flex="1">
      <Spinner size="sm" />
      <Text>Loading...</Text>
    </HStack>
  );
}

function SelectorItemContent({
  item,
  showDescription,
}: {
  item: GenericOptionItem;
  showDescription: boolean;
}) {
  return (
    <Stack flex="1">
      <Text
        truncate={true}
        maxW="full"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {item.label}
      </Text>
      {showDescription && item.description && (
        <Text color="moveon.text.muted" fontSize="xs">
          {item.description}
        </Text>
      )}
    </Stack>
  );
}

function SelectorListContent({
  portalled,
  children,
}: SelectorListContentProps) {
  return <Portal disabled={!portalled}>{children}</Portal>;
}

const createNewItem = (value: string): GenericOptionItem => ({
  label: value,
  value: NEW_ITEM_VALUE,
});

const isNewItemValue = (value: string) => value === NEW_ITEM_VALUE;

const replaceNewItemValue = (values: string[], value: string) =>
  values.map((v) => (v === NEW_ITEM_VALUE ? value : v));

const getNewItemData = (inputValue: string): GenericOptionItem => ({
  label: inputValue,
  value: inputValue,
  isNew: true,
});

const updateItems = (
  v: GenericOptionItem[],
  i: GenericOptionItem,
  mode: "prepend" | "append",
) => {
  return mode === "prepend" ? [i, ...v] : [...v, i];
};

type CreatableSelectorProps<T> = {
  previewItems: GenericOptionItem[];
  selectedValues: string[];
  stateObject: T[];
  stateObjectSetter: (value: T[]) => void;
  allowMultiple: boolean;
  onCreateItem: (label: string) => T | null | undefined;
  createOptionMode: "prepend" | "append";
  handleValueChange: (e: { value: string | string[] }) => void;
  handleClear: () => void;
  labelText?: string;
  placeholder: string;
  width?: Tokens["sizes"];
  inputId?: string;
  inputName?: string;
  showDescription: boolean;
  suppressDefaultStyles: boolean;
  isLoading: boolean;
  disableClear: boolean;
  minWidthItemList: Tokens["sizes"];
  portalled: boolean;
  listMaxH?: string;
};

function CreatableSelector<T>({
  previewItems,
  selectedValues,
  stateObject,
  stateObjectSetter,
  allowMultiple,
  onCreateItem,
  createOptionMode,
  handleValueChange,
  handleClear,
  labelText,
  placeholder,
  width,
  inputId,
  inputName,
  showDescription,
  suppressDefaultStyles,
  isLoading,
  disableClear,
  minWidthItemList,
  portalled,
  listMaxH,
}: CreatableSelectorProps<T>) {
  const [localItems, setLocalItems] =
    useState<GenericOptionItem[]>(previewItems);
  const itemsRef = useRef<GenericOptionItem[]>(previewItems);
  const [selectedValue, setSelectedValue] = useState<string[]>(selectedValues);
  const { contains } = useFilter({ sensitivity: "base" });

  useEffect(() => {
    itemsRef.current = previewItems;
    setLocalItems(previewItems);
  }, [previewItems]);

  useEffect(() => {
    setSelectedValue(selectedValues);
  }, [selectedValues]);

  const filterFn = useCallback(
    (item: GenericOptionItem, query: string) =>
      !isNewItemValue(item.value) && contains(item.label, query),
    [contains],
  );

  const collection = useMemo(
    () =>
      createListCollection({
        items: localItems,
        itemToString: (item) => item.label,
        itemToValue: (item) => item.value,
      }),
    [localItems],
  );

  const isValidNewItem = useCallback((inputValue: string) => {
    const exactOptionMatch =
      itemsRef.current.filter(
        (item) => item.label.toLowerCase() === inputValue.toLowerCase(),
      ).length > 0;
    return !exactOptionMatch && inputValue.trim().length > 0;
  }, []);

  const filter = useCallback(
    (query: string) => {
      if (isValidNewItem(query)) {
        const newItem = createNewItem(query);
        const filtered = itemsRef.current.filter((item) =>
          filterFn(item, query),
        );
        setLocalItems(updateItems(filtered, newItem, createOptionMode));
        return;
      }

      if (query.trim().length === 0) {
        setLocalItems(itemsRef.current);
      } else {
        const filtered = itemsRef.current.filter((item) =>
          filterFn(item, query),
        );
        setLocalItems(filtered);
      }
    },
    [createOptionMode, filterFn, isValidNewItem],
  );

  const selectNewItem = useCallback(
    (inputValue: string) => {
      const newT = onCreateItem(inputValue);
      if (!newT) return;

      const newItem = getNewItemData(inputValue);
      const filtered = itemsRef.current.filter(
        (item) => !isNewItemValue(item.value),
      );

      itemsRef.current = updateItems(filtered, newItem, createOptionMode);
      setLocalItems(itemsRef.current);
      stateObjectSetter(allowMultiple ? [...stateObject, newT] : [newT]);
    },
    [
      allowMultiple,
      createOptionMode,
      onCreateItem,
      stateObject,
      stateObjectSetter,
    ],
  );

  const combobox = useCombobox({
    collection,
    allowCustomValue: true,
    multiple: allowMultiple,
    onInputValueChange: (details: SelectorInputValueChangeDetails) => {
      const { inputValue, reason } = details;
      if (reason === "input-change" || reason === "item-select") {
        flushSync(() => filter(inputValue));
      }
    },
    onOpenChange(details: SelectorOpenChangeDetails) {
      const { reason, open } = details;
      if (reason === "trigger-click") {
        setLocalItems(itemsRef.current);
      }

      if (!open && selectedValue.length > 0) {
        const inputValue = collection.stringify(selectedValue[0]) || "";
        combobox.setInputValue(inputValue);
      }
    },
    value: selectedValue,
    onValueChange(details: SelectorValueChangeDetails) {
      const { value } = details;
      const inputValue = combobox.inputValue;
      const newValue = replaceNewItemValue(value, inputValue);
      setSelectedValue(newValue);
      if (value.includes(NEW_ITEM_VALUE)) {
        selectNewItem(inputValue);
      } else {
        handleValueChange({ value: newValue });
      }
    },
  });

  return (
    <Stack gap="0" w={width}>
      <Combobox.RootProvider value={combobox}>
        {labelText && <Combobox.Label>{labelText}</Combobox.Label>}
        <Combobox.Control>
          {isLoading ? (
            <LoadingIndicator />
          ) : (
            <Combobox.Input
              id={inputId}
              name={inputName}
              {...(suppressDefaultStyles
                ? {
                    style: {
                      border: "none",
                    },
                  }
                : {})}
              placeholder={placeholder}
            />
          )}
          <Combobox.IndicatorGroup>
            {!disableClear && !isLoading && (
              <Combobox.ClearTrigger onClick={handleClear} />
            )}
            <Combobox.Trigger />
          </Combobox.IndicatorGroup>
        </Combobox.Control>

        <SelectorListContent portalled={portalled}>
          <Combobox.Positioner>
            <Combobox.Content
              zIndex="max"
              minW={minWidthItemList}
              pointerEvents="auto"
              maxH={listMaxH}
              overflowY={listMaxH ? "auto" : undefined}
            >
              {combobox.collection.items.map((item) => (
                <Combobox.Item key={item.value} item={item}>
                  {isNewItemValue(item.value) ? (
                    <Combobox.ItemText>
                      {`+ Create "${item.label}"`}
                    </Combobox.ItemText>
                  ) : (
                    <HStack justify="space-between" flex="1">
                      <SelectorItemContent
                        item={item}
                        showDescription={showDescription}
                      />
                      {item.isNew && <Span textStyle="xs">NEW</Span>}
                    </HStack>
                  )}
                  <Combobox.ItemIndicator />
                </Combobox.Item>
              ))}
            </Combobox.Content>
          </Combobox.Positioner>
        </SelectorListContent>
      </Combobox.RootProvider>
    </Stack>
  );
}

/**
 * GenericSelector — reusable, memoized selector component.
 * Works as either Chakra UI Select (static), Combobox (searchable), or Creatable (user can add new options).
 */
function GenericSelectorInner<T>({
  previewItems,
  stateObject,
  stateObjectSetter,
  initValue,
  sourceItems,
  itemKey = "id" as keyof T,
  getItemByValueCustom,
  disabled = false,
  disableClear = false,
  allowMultiple = false,
  isLoading = false,
  onClear,
  onCreateItem,
  createOptionMode = "prepend",
  labelText,
  mode = "select",
  placeholder = "Choose an option",
  width,
  inputId,
  inputName,
  showDescription = false,
  suppressDefaultStyles = false,
  minWidthItemList = DEFAULT_MIN_WIDTH_ITEM_LIST,
  portalled = true,
  listMaxH,
}: GenericSelectorProps<T>) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (stateObject.length !== 0) return;

    const timeoutId = window.setTimeout(() => {
      setOpen(false);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [stateObject.length]);

  const { contains } = useFilter({ sensitivity: "base" });
  const { collection: defaultCollection, filter } = useListCollection({
    initialItems: previewItems,
    filter: contains,
  });

  useEffect(() => {
    if (stateObject.length === 0 && initValue) {
      stateObjectSetter([initValue]);
    }
  }, [initValue, stateObject.length, stateObjectSetter]);

  const handleInputChange = useCallback(
    (e: SelectorInputValueChangeDetails) => filter(e.inputValue),
    [filter],
  );

  const selectedValues = useMemo(
    () =>
      stateObject.map((v: T) =>
        v && typeof v === "object" && "id" in v
          ? (v as { id: string }).id
          : (v as string),
      ),
    [stateObject],
  );

  const getItemByValueInternal = useMemo(() => {
    if (!sourceItems) return undefined;

    return (value: string) =>
      sourceItems.find((item) => String(item[itemKey]) === value);
  }, [sourceItems, itemKey]);

  const getItemByValue = getItemByValueCustom || getItemByValueInternal;

  useEffect(() => {
    if (showDescription) {
      const missing = previewItems.some(
        (p) => p.description === undefined || p.description === null,
      );
      if (missing) {
        throw new Error(
          "GenericSelector: `showDescription=true` but some `previewItems` are missing the `description` field.",
        );
      }
    }
  }, [showDescription, previewItems]);

  const handleValueChange = useCallback(
    (e: { value: string | string[] }) => {
      const newValues = Array.isArray(e.value) ? e.value : [e.value];

      if (getItemByValue) {
        if (allowMultiple) {
          const foundItems = newValues
            .map((val: string) => getItemByValue(val))
            .filter(Boolean) as T[];
          stateObjectSetter(foundItems);
        } else {
          const found =
            newValues.length > 0 ? getItemByValue(newValues[0]) : undefined;
          stateObjectSetter(found ? [found] : []);
        }
      } else {
        // When no getItemByValue is provided, use values directly
        stateObjectSetter(
          allowMultiple ? (newValues as T[]) : [(newValues[0] || "") as T],
        );
      }
    },
    [getItemByValue, stateObjectSetter, allowMultiple],
  );

  const handleClear = useCallback(() => {
    stateObjectSetter([]);
    if (onClear) {
      onClear();
    }
  }, [onClear, stateObjectSetter]);

  if (mode === "creatable") {
    if (!onCreateItem) {
      throw new Error(
        "GenericSelector: 'creatable' mode requires 'onCreateItem' prop to be provided.",
      );
    }
    return (
      <CreatableSelector
        previewItems={previewItems}
        selectedValues={selectedValues}
        stateObject={stateObject}
        stateObjectSetter={stateObjectSetter}
        allowMultiple={allowMultiple}
        onCreateItem={onCreateItem}
        createOptionMode={createOptionMode}
        handleValueChange={handleValueChange}
        handleClear={handleClear}
        labelText={labelText}
        placeholder={placeholder}
        width={width}
        inputId={inputId}
        inputName={inputName}
        showDescription={showDescription}
        suppressDefaultStyles={suppressDefaultStyles}
        isLoading={isLoading}
        disableClear={disableClear}
        minWidthItemList={minWidthItemList}
        portalled={portalled}
        listMaxH={listMaxH}
      />
    );
  }

  if (mode === "combobox") {
    if (allowMultiple) {
      throw new Error(
        "GenericSelector: 'combobox' mode does YET not support 'allowMultiple=true'. Fix it or turn it off.",
      );
    }

    return (
      <Combobox.Root
        w={width}
        collection={defaultCollection}
        onInputValueChange={handleInputChange}
        value={selectedValues}
        onValueChange={handleValueChange}
        openOnClick
        openOnChange={(e: { inputValue: string }) => e.inputValue.length > 2}
        multiple={allowMultiple}
        disabled={disabled || isLoading}
        open={open}
        onOpenChange={(details: SelectorOpenChangeDetails) =>
          setOpen(details.open)
        }
      >
        {labelText && <Combobox.Label>{labelText}</Combobox.Label>}

        <Combobox.Control>
          {isLoading ? (
            <LoadingIndicator />
          ) : (
            <Combobox.Input
              id={inputId}
              name={inputName}
              {...(suppressDefaultStyles
                ? {
                    style: {
                      border: "none",
                      // TODO : there is more to suppres in combobox default styles .. finetune as needed
                    },
                  }
                : {})}
              placeholder={placeholder}
            />
          )}
          <Combobox.IndicatorGroup>
            {!disableClear && !isLoading && (
              <Combobox.ClearTrigger onClick={handleClear} />
            )}
            <Combobox.Trigger />
          </Combobox.IndicatorGroup>
        </Combobox.Control>

        <SelectorListContent portalled={portalled}>
          <Combobox.Positioner>
            <Combobox.Content
              zIndex="max"
              minW={minWidthItemList}
              pointerEvents="auto"
              maxH={listMaxH}
              overflowY={listMaxH ? "auto" : undefined}
            >
              {defaultCollection.items.map((item) => (
                <HStack
                  key={item.value}
                  width="full"
                  justify="space-between"
                  align="center"
                >
                  <Combobox.Item
                    item={item}
                    key={item.value}
                    width="full"
                    cursor="pointer"
                  >
                    <SelectorItemContent
                      item={item}
                      showDescription={showDescription}
                    />
                    <Combobox.ItemIndicator />
                  </Combobox.Item>
                </HStack>
              ))}
            </Combobox.Content>
          </Combobox.Positioner>
        </SelectorListContent>
      </Combobox.Root>
    );
  }

  return (
    <Select.Root
      w={width}
      collection={defaultCollection}
      value={selectedValues}
      onValueChange={handleValueChange}
      multiple={allowMultiple}
      disabled={disabled || isLoading}
      open={open}
      onOpenChange={(details: SelectorOpenChangeDetails) =>
        setOpen(details.open)
      }
    >
      <Select.HiddenSelect id={inputId} name={inputName} />
      {labelText && <Select.Label>{labelText}</Select.Label>}

      <Select.Control>
        <Select.Trigger
          {...(suppressDefaultStyles
            ? {
                style: {
                  border: "none",
                },
              }
            : {})}
        >
          {isLoading ? (
            <LoadingIndicator />
          ) : (
            <Select.ValueText
              placeholder={placeholder}
              truncate={true}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            />
          )}
        </Select.Trigger>
        <Select.IndicatorGroup>
          {!disableClear && !isLoading && (
            <Select.ClearTrigger onClick={handleClear} />
          )}
        </Select.IndicatorGroup>
      </Select.Control>

      <SelectorListContent portalled={portalled}>
        <Select.Positioner>
          <Select.Content
            zIndex="max"
            minW={minWidthItemList}
            pointerEvents="auto"
            maxH={listMaxH}
            overflowY={listMaxH ? "auto" : undefined}
          >
            {defaultCollection.items.map((item) => (
              <HStack
                key={item.value}
                width="full"
                justify="space-between"
                align="center"
              >
                <Select.Item
                  item={item}
                  key={item.value}
                  width="full"
                  cursor="pointer"
                >
                  <SelectorItemContent
                    item={item}
                    showDescription={showDescription}
                  />
                  <Select.ItemIndicator />
                </Select.Item>
              </HStack>
            ))}
          </Select.Content>
        </Select.Positioner>
      </SelectorListContent>
    </Select.Root>
  );
}

const GenericSelector = memo(GenericSelectorInner) as <T>(
  props: GenericSelectorProps<T>,
) => JSX.Element;

export default GenericSelector;
